import React, { useEffect, useMemo, useState, memo, createElement } from 'react';
import { Download, Package, XCircle, Check, Loader2, Plus, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, MoreVertical, Clock, Building2, User, CheckSquare, Square, MinusSquare, Hash, Receipt, Percent, BadgeDollarSign, HandCoins, Banknote, ChevronRight, X, AlertTriangle, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MetricsBar } from '../components/MetricsBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { SearchBar } from '../components/ui/SearchBar';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { filterTodayOrders } from '../utils/dateFilters';
import { cleanProductName, displayProductName } from '../utils/formatters';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { MenuItemOptionsModal } from '../components/MenuItemOptionsModal';
import { useHasPermission } from '../utils/permissions';
import { exportOrdersToExcel } from '../utils/excelExport';
import { CreateOrderModal } from '../components/CreateOrderModal';
import { getStatusColor, calculateTax, calculateSubtotal } from '../utils/orderHelpers';
// Helper: Split combined product strings into individual products
function splitCombinedProducts(productStr: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  for (const char of productStr) {
    if (char === '(') depth++;else if (char === ')') depth--;else if (char === ',' && depth === 0) {
      if (current.trim()) parts.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}
// Helper: Expand a single combined order into multiple display rows
function expandCombinedOrders(orders: any[]): any[] {
  return orders.flatMap(order => {
    const products = splitCombinedProducts(order.product);
    if (products.length > 1 && order.quantity >= products.length) {
      const perItemTotal = parseFloat(order.total) / products.length;
      const perItemSubtotal = parseFloat(order.subtotal || order.total) / products.length;
      const perItemSubsidy = parseFloat(order.subsidyAmount || '0') / products.length;
      const perItemDiscount = parseFloat(order.discount || '0') / products.length;
      const qtyPerItem = Math.floor(order.quantity / products.length);
      return products.map((prod: string, idx: number) => ({
        ...order,
        id: `${order.id}_split_${idx}`,
        _originalId: order.id,
        _originalOrderNo: order.orderNo,
        product: prod,
        quantity: qtyPerItem,
        total: perItemTotal.toFixed(2),
        subtotal: perItemSubtotal.toFixed(2),
        subsidyAmount: perItemSubsidy.toFixed(2),
        discount: perItemDiscount.toFixed(2)
      }));
    }
    return [{
      ...order,
      _originalId: order.id
    }];
  });
}
// Helper: Extract time from orderDate string
function extractTimeFromDate(orderDate: string): string {
  if (!orderDate) return '';
  const parts = orderDate.split(',');
  if (parts.length > 1) {
    return parts[1].trim();
  }
  return '';
}
export function OrdersDashboard() {
  useDocumentTitle('Órdenes');
  // Permission checks
  const canCreate = useHasPermission('orders.create');
  const canEdit = useHasPermission('orders.edit');
  const canDelete = useHasPermission('orders.delete');
  const canExport = useHasPermission('orders.export');
  const {
    filteredOrders,
    orders,
    loading,
    cancelOrder,
    updateOrderStatus,
    bulkUpdateOrderStatus,
    addOrder,
    refreshOrders
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('Todas');
  const [branchFilter, setBranchFilter] = useState('Todas');
  const [dateFilter, setDateFilter] = useState<'today' | 'all'>('today');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [detailOrder, setDetailOrder] = useState<any>(null);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  // Ordenamiento
  const [sortColumn, setSortColumn] = useState<string>('orderNo');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  // Selección masiva
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const companies = getCompanies();
  const companyNames = ['Todas', ...companies.map(c => c.name)];
  // Check if any filter is active
  const hasActiveFilters = companyFilter !== 'Todas' || branchFilter !== 'Todas' || searchTerm !== '' || statusFilter !== 'Todos';
  // Clear all filters
  const clearAllFilters = () => {
    setCompanyFilter('Todas');
    setBranchFilter('Todas');
    setSearchTerm('');
    setStatusFilter('Todos');
  };
  // --- LISTENER: Mini-notificaciones visuales cuando llega una nueva orden ---
  useEffect(() => {
    const handleNewOrder = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        showToast('info', `Nueva Orden #${detail.orderNo}`, `${detail.name} — ${detail.product} (${detail.company})`);
      }
    };
    window.addEventListener('cricket-new-order', handleNewOrder);
    return () => window.removeEventListener('cricket-new-order', handleNewOrder);
  }, [showToast]);
  // Sincronización en tiempo real
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const subscription = supabaseClient.subscribeToOrders(() => {
      setIsAutoRefreshing(true);
      setTimeout(() => setIsAutoRefreshing(false), 800);
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  // Load menu items and employees for order creation
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isSupabaseConfigured()) {
          const [items, emps] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getEmployees()]);
          setMenuItems(items || []);
          setEmployees(emps || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);
  // Get branches for selected company
  const availableBranches = useMemo(() => {
    if (companyFilter === 'Todas') {
      return ['Todas'];
    }
    const company = companies.find(c => c.name === companyFilter);
    const branchNames = new Set<string>();
    if (company?.branches) {
      company.branches.forEach((b: any) => {
        if (b.name) branchNames.add(b.name);
      });
    }
    return ['Todas', ...Array.from(branchNames).sort()];
  }, [companyFilter, companies]);
  // Reset branch filter when company changes
  useEffect(() => {
    setBranchFilter('Todas');
  }, [companyFilter]);
  // Filter orders by date, company and branch
  const baseFilteredOrders = useMemo(() => {
    let filtered = filteredOrders(searchTerm, companyFilter);
    if (dateFilter === 'today') {
      filtered = filterTodayOrders(filtered);
    }
    if (branchFilter !== 'Todas') {
      filtered = filtered.filter(order => order.branch === branchFilter);
    }
    return expandCombinedOrders(filtered);
  }, [filteredOrders, searchTerm, companyFilter, branchFilter, dateFilter]);
  // Status counts for tabs
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      Todos: baseFilteredOrders.length,
      Pendiente: 0,
      'En Preparación': 0,
      Listo: 0,
      Entregado: 0,
      Cancelado: 0
    };
    baseFilteredOrders.forEach(order => {
      const s = order.status || 'Pendiente';
      if (counts[s] !== undefined) counts[s]++;
    });
    return counts;
  }, [baseFilteredOrders]);
  // Final display orders with status filter + sorting
  const displayOrders = useMemo(() => {
    let filtered = baseFilteredOrders;
    if (statusFilter !== 'Todos') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    const sorted = [...filtered].sort((a, b) => {
      let valA: any;
      let valB: any;
      switch (sortColumn) {
        case 'orderNo':
          valA = a.orderNo || '';
          valB = b.orderNo || '';
          break;
        case 'orderDate':
          valA = a.orderDateISO || a.order_date || a.created_at || a.orderDate || '';
          valB = b.orderDateISO || b.order_date || b.created_at || b.orderDate || '';
          break;
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
        case 'company':
          valA = (a.company || '').toLowerCase();
          valB = (b.company || '').toLowerCase();
          break;
        case 'product':
          valA = (a.product || '').toLowerCase();
          valB = (b.product || '').toLowerCase();
          break;
        case 'quantity':
          valA = a.quantity || 0;
          valB = b.quantity || 0;
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'subsidyAmount':
          valA = parseFloat(a.subsidyAmount || '0');
          valB = parseFloat(b.subsidyAmount || '0');
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'total':
          valA = parseFloat(a.total || '0');
          valB = parseFloat(b.total || '0');
          return sortDirection === 'asc' ? valA - valB : valB - valA;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        case 'labelStatus':
          valA = (a.labelStatus || '').toLowerCase();
          valB = (b.labelStatus || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [baseFilteredOrders, statusFilter, sortColumn, sortDirection]);
  // Paginación
  const totalPages = Math.ceil(displayOrders.length / itemsPerPage);
  const paginatedOrders = displayOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter, branchFilter, dateFilter, statusFilter, sortColumn, sortDirection]);
  // Calculate totals from DISPLAYED orders only
  const metrics = useMemo(() => {
    const activeDisplayOrders = displayOrders.filter(o => o.status !== 'Cancelado');
    let totalSubtotal = 0;
    let totalTax = 0;
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalSubsidy = 0;
    for (const order of activeDisplayOrders) {
      const total = parseFloat(order.total);
      totalSubtotal += calculateSubtotal(total, order.taxExempt);
      totalTax += calculateTax(total, order.taxExempt);
      totalAmount += total;
      totalDiscount += parseFloat(order.discount || '0');
      totalSubsidy += parseFloat(order.subsidyAmount || '0');
    }
    const totalCount = activeDisplayOrders.length;
    return [{
      label: 'Cantidad',
      value: totalCount.toString(),
      icon: 'hash'
    }, {
      label: 'Subtotal',
      value: `RD$ ${totalSubtotal.toLocaleString('es-DO', {
        minimumFractionDigits: 2
      })}`,
      icon: 'receipt'
    }, {
      label: 'ITBIS (18%)',
      value: `RD$ ${totalTax.toLocaleString('es-DO', {
        minimumFractionDigits: 2
      })}`,
      icon: 'percent'
    }, {
      label: 'Descuento',
      value: `RD$ ${totalDiscount.toLocaleString('es-DO', {
        minimumFractionDigits: 2
      })}`,
      icon: 'badge'
    }, {
      label: 'Subsidios',
      value: `RD$ ${totalSubsidy.toLocaleString('es-DO', {
        minimumFractionDigits: 2
      })}`,
      icon: 'hands'
    }, {
      label: 'Total Facturado',
      value: `RD$ ${totalAmount.toLocaleString('es-DO', {
        minimumFractionDigits: 2
      })}`,
      icon: 'banknote'
    }];
  }, [displayOrders]);
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  // --- Bulk Selection Helpers ---
  const selectableOrders = useMemo(() => paginatedOrders.filter(o => o.status !== 'Cancelado' && o.status !== 'Entregado'), [paginatedOrders]);
  const toggleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedOrders.size === selectableOrders.length && selectableOrders.length > 0) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(selectableOrders.map(o => o._originalId || o.id)));
    }
  };
  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedOrders.size === 0) return;
    setBulkProcessing(true);
    const uniqueIds = new Set<string>();
    selectedOrders.forEach(id => {
      const order = displayOrders.find(o => o.id === id || o._originalId === id);
      uniqueIds.add(order?._originalId || id);
    });
    const idsArray = Array.from(uniqueIds);
    try {
      if (newStatus === 'Cancelado') {
        const results = await Promise.allSettled(idsArray.map(id => cancelOrder(id)));
        const successCount = results.filter(r => r.status === 'fulfilled').length;
        const errorCount = results.filter(r => r.status === 'rejected').length;
        if (successCount > 0) {
          addLog('Anulación Masiva', `${successCount} órdenes anuladas`, 'order');
          showToast('success', 'Órdenes anuladas', `${successCount} orden(es) anulada(s)${errorCount > 0 ? ` (${errorCount} error(es))` : ''}`);
        } else {
          showToast('error', 'Error', 'No se pudieron anular las órdenes');
        }
      } else {
        const {
          successCount,
          errorCount
        } = await bulkUpdateOrderStatus(idsArray, newStatus as any);
        if (successCount > 0) {
          addLog('Cambio Masivo de Estado', `${successCount} órdenes cambiadas a "${newStatus}"`, 'order');
          showToast('success', 'Estado actualizado', `${successCount} orden(es) cambiada(s) a "${newStatus}"${errorCount > 0 ? ` (${errorCount} error(es))` : ''}`);
        } else {
          showToast('error', 'Error', 'No se pudieron actualizar las órdenes');
        }
      }
    } catch (error) {
      showToast('error', 'Error', 'No se pudieron actualizar las órdenes');
    }
    setSelectedOrders(new Set());
    setShowBulkStatusModal(false);
    setBulkProcessing(false);
  };
  // Clear selection when filters change
  useEffect(() => {
    setSelectedOrders(new Set());
  }, [searchTerm, companyFilter, branchFilter, dateFilter, statusFilter, currentPage]);
  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={12} className="text-gray-300 dark:text-gray-600 ml-1 inline-block" data-id="element-4936" />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={12} className="text-blue-600 dark:text-blue-400 ml-1 inline-block" data-id="element-4937" /> : <ArrowDown size={12} className="text-blue-600 dark:text-blue-400 ml-1 inline-block" data-id="element-4938" />;
  };
  const handleCancelOrder = (order: any) => {
    const originalOrder = order._originalId ? {
      ...order,
      id: order._originalId
    } : order;
    setSelectedOrder(originalOrder);
    setShowCancelModal(true);
  };
  const handleChangeStatus = (order: any) => {
    const originalOrder = order._originalId ? {
      ...order,
      id: order._originalId
    } : order;
    setSelectedOrder(originalOrder);
    setShowStatusModal(true);
  };
  const confirmCancel = async () => {
    if (selectedOrder) {
      try {
        await cancelOrder(selectedOrder.id);
        addLog('Pedido Anulado', `Orden #${selectedOrder.orderNo} - ${selectedOrder.name} - ${selectedOrder.company}`, 'order');
        showToast('success', 'Pedido anulado', `Orden #${selectedOrder.orderNo} ha sido anulada`);
        setShowCancelModal(false);
        setSelectedOrder(null);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo anular el pedido');
      }
    }
  };
  const getNextStatus = (current: string) => {
    if (current === 'Pendiente') return 'En Preparación';
    if (current === 'En Preparación') return 'Listo';
    if (current === 'Listo') return 'Entregado';
    return null;
  };
  const handleAdvanceStatus = async (e: React.MouseEvent, order: any) => {
    e.stopPropagation();
    const nextStatus = getNextStatus(order.status);
    if (nextStatus && canEdit) {
      try {
        const originalId = order._originalId || order.id;
        await updateOrderStatus(originalId, nextStatus);
        addLog('Estado de Pedido Avanzado', `Orden #${order.orderNo} avanzó a ${nextStatus}`, 'order');
        showToast('success', 'Estado actualizado', `Orden #${order.orderNo} avanzó a ${nextStatus}`);
        if (detailOrder && (detailOrder.id === order.id || detailOrder._originalId === order._originalId)) {
          setDetailOrder({
            ...detailOrder,
            status: nextStatus
          });
        }
      } catch (error) {
        showToast('error', 'Error', 'No se pudo actualizar el estado');
      }
    }
  };
  const confirmStatusChange = async (newStatus: any) => {
    if (selectedOrder) {
      try {
        await updateOrderStatus(selectedOrder.id, newStatus);
        addLog('Estado de Pedido Actualizado', `Orden #${selectedOrder.orderNo} cambió a ${newStatus}`, 'order');
        showToast('success', 'Estado actualizado', `Orden #${selectedOrder.orderNo} ahora está ${newStatus}`);
        if (detailOrder && (detailOrder.id === selectedOrder.id || detailOrder._originalId === selectedOrder._originalId)) {
          setDetailOrder({
            ...detailOrder,
            status: newStatus
          });
        }
        setShowStatusModal(false);
        setSelectedOrder(null);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo actualizar el estado');
      }
    }
  };
  const exportOrders = () => {
    const ordersToExport = displayOrders;
    if (ordersToExport.length === 0) {
      showToast('warning', 'Sin datos', 'No hay órdenes para exportar');
      return;
    }
    exportOrdersToExcel({
      filters: {
        company: companyFilter,
        branch: branchFilter,
        dateFilter: dateFilter === 'today' ? 'Solo hoy' : 'Todas'
      },
      orders: ordersToExport.map(order => {
        const orderTotal = parseFloat(order.total);
        const orderSubtotal = order.taxExempt ? orderTotal : orderTotal / 1.18;
        const orderTax = order.taxExempt ? 0 : orderTotal - orderTotal / 1.18;
        const orderSubsidy = parseFloat(order.subsidyAmount || '0');
        return {
          orderNo: order.orderNo,
          orderDate: order.orderDate,
          name: order.name,
          company: order.company,
          branch: order.branch || '-',
          product: order.product,
          quantity: order.quantity,
          subtotal: orderSubtotal,
          tax: orderTax,
          discount: parseFloat(order.discount || '0'),
          subsidy: orderSubsidy,
          total: orderTotal,
          status: order.status,
          labelStatus: order.labelStatus,
          taxExempt: order.taxExempt || false
        };
      })
    });
    showToast('success', 'Exportación exitosa', `${ordersToExport.length} órdenes exportadas a Excel`);
  };
  // Loading state
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-id="element-4939">
        <div className="flex items-center justify-center h-96" data-id="element-4940">
          <div className="text-center" data-id="element-4941">
            <div className="relative" data-id="element-4942">
              <div className="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 rounded-full" data-id="element-4943" />
              <div className="absolute inset-0 w-16 h-16 border-4 border-gray-900 dark:border-white rounded-full border-t-transparent animate-spin" data-id="element-4944" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium" data-id="element-4945">
              Cargando pedidos...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-id="element-4946">
      {/* Page Header */}
      <div className="mb-6" data-id="element-4947">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4948">
          <div data-id="element-4949">
            <div className="flex items-center gap-3" data-id="element-4950">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4951">
                Órdenes
              </h1>
              {isAutoRefreshing && <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-full" data-id="element-4952">
                  <RefreshCw className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 animate-spin" data-id="element-4953" />
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400" data-id="element-4954">
                    Actualizando
                  </span>
                </div>}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4955">
              Gestiona y supervisa todos los pedidos del sistema
            </p>
          </div>

          <div className="flex items-center gap-2" data-id="element-4956">
            {canCreate && <Button variant="outline" size="sm" leftIcon={<Plus className="w-4 h-4" data-id="element-4958" />} onClick={() => setShowCreateOrderModal(true)} data-id="element-4957">
                Nueva Orden
              </Button>}
            {canExport && <Button variant="primary" size="sm" leftIcon={<Download className="w-4 h-4" data-id="element-4960" />} onClick={exportOrders} data-id="element-4959">
                Exportar
              </Button>}
          </div>
        </div>
      </div>

      {/* Metrics */}
      <MetricsBar metrics={metrics} data-id="element-4961" />

      {/* Main Content Card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-4962">
        {/* Filters Section */}
        <div className="p-4 lg:p-5 border-b border-gray-100 dark:border-gray-800" data-id="element-4963">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-4964">
            {/* Date Toggle - Segmented Control */}
            <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-xl shrink-0" data-id="element-4965">
              <button onClick={() => setDateFilter('today')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${dateFilter === 'today' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4966">
                Solo hoy
              </button>
              <button onClick={() => setDateFilter('all')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${dateFilter === 'all' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4967">
                Todas las fechas
              </button>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-3 flex-1" data-id="element-4968">
              <FilterDropdown value={companyFilter} options={companyNames} onChange={setCompanyFilter} className="w-full xs:w-auto min-w-[160px]" data-id="element-4969" />

              {companyFilter !== 'Todas' && <FilterDropdown value={branchFilter} options={availableBranches} onChange={setBranchFilter} className="w-full xs:w-auto min-w-[140px]" data-id="element-4970" />}

              <div className="flex-1 min-w-[200px]" data-id="element-4971">
                <SearchBar className="w-full" onSearch={setSearchTerm} placeholder="Buscar por nombre, orden, producto..." data-id="element-4972" />
              </div>

              {/* Clear filters button */}
              {hasActiveFilters && <button onClick={clearAllFilters} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-4973">
                  <X size={14} data-id="element-4974" />
                  Limpiar filtros
                </button>}
            </div>
          </div>

          {/* Results count */}
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-4975">
            {displayOrders.length}{' '}
            {displayOrders.length === 1 ? 'orden encontrada' : 'órdenes encontradas'}
          </div>

          {/* Status Tabs */}
          <div className="mt-4 -mb-px border-b border-gray-200 dark:border-gray-700" data-id="element-4976">
            <div className="relative flex items-center gap-1 overflow-x-auto pb-px scrollbar-hide" data-id="element-4977">
              {(['Todos', 'Pendiente', 'En Preparación', 'Listo', 'Entregado', 'Cancelado'] as const).map(status => {
              const count = statusCounts[status] || 0;
              const isActive = statusFilter === status;
              const statusColors = getStatusColor(status);
              const dotColor = statusColors.dot;
              const isDisabled = status !== 'Todos' && count === 0;
              return <button key={status} onClick={() => !isDisabled && setStatusFilter(status)} disabled={isDisabled} className={`
                      relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap transition-all
                      ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}
                    `} data-id="element-4978">
                    {isActive && <motion.div layoutId="activeStatusTab" className="absolute inset-0 bg-gray-50 dark:bg-gray-800 rounded-t-lg border-t border-l border-r border-gray-200 dark:border-gray-700" style={{
                  bottom: -1
                }} transition={{
                  type: 'spring',
                  bounce: 0.15,
                  duration: 0.4
                }} data-id="element-4979" />}
                    <span className={`relative z-10 flex items-center gap-2 ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4980">
                      {status !== 'Todos' && <span className={`w-2 h-2 rounded-full ${dotColor}`} data-id="element-4981" />}
                      {status}
                      <span className={`text-xs px-1.5 py-0.5 rounded-md font-mono font-bold ${isActive ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500'}`} data-id="element-4982">
                        {count}
                      </span>
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-5" data-id="element-4983">
          {orders.length === 0 ? <div className="text-center py-16" data-id="element-4984">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-4985">
                <Package size={40} className="text-gray-400 dark:text-gray-500" data-id="element-4986" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-4987">
                No hay órdenes registradas
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto" data-id="element-4988">
                Las órdenes aparecerán aquí cuando los empleados hagan pedidos.
              </p>
            </div> : displayOrders.length === 0 ? <div className="text-center py-16" data-id="element-4989">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-4990">
                <Package size={40} className="text-gray-400 dark:text-gray-500" data-id="element-4991" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-4992">
                {statusFilter !== 'Todos' ? `No hay órdenes con estado "${statusFilter}"` : dateFilter === 'today' ? 'No hay pedidos para hoy' : 'No se encontraron órdenes'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto" data-id="element-4993">
                {statusFilter !== 'Todos' ? 'Prueba seleccionando otro estado o ajusta los filtros' : dateFilter === 'today' ? 'Los pedidos de hoy aparecerán aquí cuando se registren.' : 'Intenta ajustar los filtros de búsqueda'}
              </p>
            </div> : <>
              {/* Desktop Table View */}
              <div className="hidden xl:block overflow-x-auto" data-id="element-4994">
                <table className="w-full text-left border-collapse min-w-[1200px]" data-id="element-4995">
                  <thead data-id="element-4996">
                    <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-4997">
                      <th className="px-3 py-3 w-10" data-id="element-4998">
                        <button onClick={toggleSelectAll} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" title={selectedOrders.size === selectableOrders.length && selectableOrders.length > 0 ? 'Deseleccionar todo' : 'Seleccionar todo'} data-id="element-4999">
                          {selectedOrders.size === 0 ? <Square size={18} data-id="element-5000" /> : selectedOrders.size === selectableOrders.length ? <CheckSquare size={18} className="text-blue-600" data-id="element-5001" /> : <MinusSquare size={18} className="text-blue-600" data-id="element-5002" />}
                        </button>
                      </th>
                      <th onClick={() => handleSort('orderNo')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5003">
                        Orden {renderSortIcon('orderNo')}
                      </th>
                      <th onClick={() => handleSort('orderDate')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5004">
                        Fecha {renderSortIcon('orderDate')}
                      </th>
                      <th onClick={() => handleSort('name')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5005">
                        Cliente {renderSortIcon('name')}
                      </th>
                      <th onClick={() => handleSort('company')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5006">
                        Empresa {renderSortIcon('company')}
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap" data-id="element-5007">
                        Sucursal
                      </th>
                      <th onClick={() => handleSort('product')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5008">
                        Producto {renderSortIcon('product')}
                      </th>
                      <th onClick={() => handleSort('quantity')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap text-center" data-id="element-5009">
                        Cant. {renderSortIcon('quantity')}
                      </th>
                      <th onClick={() => handleSort('total')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5010">
                        Total {renderSortIcon('total')}
                      </th>
                      <th onClick={() => handleSort('status')} className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-gray-900 dark:hover:text-white select-none transition-colors whitespace-nowrap" data-id="element-5011">
                        Estado {renderSortIcon('status')}
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right whitespace-nowrap" data-id="element-5012">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5013">
                    {paginatedOrders.map((order, idx) => {
                  const isSelected = selectedOrders.has(order._originalId || order.id);
                  const statusColors = getStatusColor(order.status);
                  return <tr key={order.id} onClick={() => setDetailOrder(order)} className={`
                            group cursor-pointer transition-all
                            ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/30'}
                            hover:bg-blue-50/50 dark:hover:bg-blue-900/10
                            ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                          `} style={{
                    borderLeft: `3px solid`,
                    borderLeftColor: order.status === 'Pendiente' ? '#eab308' : order.status === 'En Preparación' ? '#3b82f6' : order.status === 'Listo' ? '#22c55e' : order.status === 'Entregado' ? '#6b7280' : order.status === 'Cancelado' ? '#ef4444' : '#e5e7eb'
                  }} data-id="element-5014">
                          <td className="px-3 py-3" data-id="element-5015">
                            {order.status !== 'Cancelado' && order.status !== 'Entregado' ? <button onClick={e => {
                        e.stopPropagation();
                        toggleSelectOrder(order._originalId || order.id);
                      }} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" data-id="element-5016">
                                {isSelected ? <CheckSquare size={18} className="text-blue-600" data-id="element-5017" /> : <Square size={18} data-id="element-5018" />}
                              </button> : <div className="w-[18px]" data-id="element-5019" />}
                          </td>
                          <td className="px-3 py-3" data-id="element-5020">
                            <span className="text-sm font-mono font-semibold text-gray-900 dark:text-white" data-id="element-5021">
                              #{order.orderNo}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5022">
                            <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5023">
                              {order.orderDate?.split(',')[0] || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5024">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate block max-w-[180px]" title={order.name} data-id="element-5025">
                              {order.name}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5026">
                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate block max-w-[160px]" title={order.company} data-id="element-5027">
                              {order.company}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5028">
                            <span className="text-sm text-gray-500 dark:text-gray-400 truncate block max-w-[120px]" data-id="element-5029">
                              {order.branch || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5030">
                            <span className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 max-w-[320px]" title={cleanProductName(order.product)} data-id="element-5031">
                              {cleanProductName(order.product)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center" data-id="element-5032">
                            <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5033">
                              {order.quantity}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5034">
                            <span className="text-sm font-mono font-semibold text-gray-900 dark:text-white" data-id="element-5035">
                              RD$ {parseFloat(order.total).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 py-3" data-id="element-5036">
                            <div className="flex items-center gap-1.5" data-id="element-5037">
                              <button onClick={e => {
                          e.stopPropagation();
                          handleChangeStatus(order);
                        }} disabled={order.status === 'Cancelado' || !canEdit} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${statusColors.bg} ${statusColors.text}`} data-id="element-5038">
                                <span className={`w-1.5 h-1.5 rounded-full ${statusColors.dot}`} data-id="element-5039" />
                                {order.status}
                              </button>
                              {order.status !== 'Cancelado' && order.status !== 'Entregado' && canEdit && <button onClick={e => handleAdvanceStatus(e, order)} className="p-1 text-gray-300 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-all" title={`Avanzar a ${getNextStatus(order.status)}`} data-id="element-5040">
                                    <ChevronRight size={16} data-id="element-5041" />
                                  </button>}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right" data-id="element-5042">
                            {order.status !== 'Cancelado' && order.status !== 'Entregado' && canDelete && <button onClick={e => {
                        e.stopPropagation();
                        handleCancelOrder(order);
                      }} className="text-xs font-medium text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors" data-id="element-5043">
                                  Anular
                                </button>}
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>

              {/* Mobile & Tablet Card View */}
              <div className="xl:hidden space-y-3" data-id="element-5044">
                {paginatedOrders.map(order => {
              const statusColors = getStatusColor(order.status);
              const isSelected = selectedOrders.has(order._originalId || order.id);
              const orderTime = extractTimeFromDate(order.orderDate);
              return <div key={order.id} onClick={() => setDetailOrder(order)} className={`
                        cursor-pointer bg-white dark:bg-gray-900 rounded-xl border overflow-hidden
                        transition-shadow hover:shadow-md
                        ${isSelected ? 'ring-2 ring-blue-500 border-blue-300 dark:border-blue-700' : 'border-gray-100 dark:border-gray-800'}
                      `} data-id="element-5045">
                      {/* Status accent bar */}
                      <div className={`h-1 bg-gradient-to-r ${statusColors.gradient}`} data-id="element-5046" />

                      <div className="p-4" data-id="element-5047">
                        {/* Header row */}
                        <div className="flex items-center justify-between gap-3 mb-3" data-id="element-5048">
                          <div className="flex items-center gap-3 min-w-0" data-id="element-5049">
                            {order.status !== 'Cancelado' && order.status !== 'Entregado' && <button onClick={e => {
                        e.stopPropagation();
                        toggleSelectOrder(order._originalId || order.id);
                      }} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0" data-id="element-5050">
                                  {isSelected ? <CheckSquare size={20} className="text-blue-600" data-id="element-5051" /> : <Square size={20} data-id="element-5052" />}
                                </button>}
                            <div className="min-w-0" data-id="element-5053">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-5054">
                                <span className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400" data-id="element-5055">
                                  #{order.orderNo}
                                </span>
                                <span className="text-xs text-gray-400 dark:text-gray-500" data-id="element-5056">
                                  {order.orderDate?.split(',')[0]}
                                </span>
                                {orderTime && <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded" data-id="element-5057">
                                    {orderTime}
                                  </span>}
                              </div>
                              <h3 className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-5058">
                                {order.name}
                              </h3>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0" data-id="element-5059">
                            <button onClick={e => {
                        e.stopPropagation();
                        handleChangeStatus(order);
                      }} disabled={order.status === 'Cancelado' || !canEdit} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap ${statusColors.bg} ${statusColors.text}`} data-id="element-5060">
                              <span className={`w-2 h-2 rounded-full ${statusColors.dot}`} data-id="element-5061" />
                              {order.status}
                            </button>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 mb-3" data-id="element-5062">
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-5063">
                            <Building2 size={14} className="shrink-0" data-id="element-5064" />
                            <span className="truncate" data-id="element-5065">
                              {order.company}
                              {order.branch ? ` · ${order.branch}` : ''}
                            </span>
                          </div>
                          <div className="flex items-start gap-2 text-sm" data-id="element-5066">
                            <Package size={14} className="text-gray-400 shrink-0 mt-0.5" data-id="element-5067" />
                            <span className="text-gray-700 dark:text-gray-300" data-id="element-5068">
                              {cleanProductName(order.product)}
                            </span>
                            <span className="text-gray-400 shrink-0 ml-auto" data-id="element-5069">
                              ×{order.quantity}
                            </span>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-5070">
                          <span className="text-lg font-bold font-mono text-gray-900 dark:text-white" data-id="element-5071">
                            RD$ {parseFloat(order.total).toFixed(2)}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-5072">
                            {order.status !== 'Cancelado' && order.status !== 'Entregado' && canEdit && <button onClick={e => handleAdvanceStatus(e, order)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all" title={`Avanzar a ${getNextStatus(order.status)}`} data-id="element-5073">
                                  <ChevronRight size={16} data-id="element-5074" />
                                </button>}
                            {order.status !== 'Cancelado' && order.status !== 'Entregado' && canDelete && <button onClick={e => {
                        e.stopPropagation();
                        handleCancelOrder(order);
                      }} className="text-xs font-medium text-gray-400 hover:text-red-600 dark:hover:text-red-400 px-2 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-5075">
                                  Anular
                                </button>}
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && <div className="mt-6" data-id="element-5076">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={displayOrders.length} itemsPerPage={itemsPerPage} data-id="element-5077" />
                </div>}
            </>}
        </div>
      </div>

      {/* Bulk Action Bar */}
      <AnimatePresence data-id="element-5078">
        {selectedOrders.size > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40" data-id="element-5079">
            <div className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-5 py-3 rounded-2xl shadow-2xl shadow-gray-900/30 dark:shadow-white/20 border border-gray-700 dark:border-gray-200 flex items-center gap-4" data-id="element-5080">
              <span className="text-sm font-medium whitespace-nowrap" data-id="element-5081">
                <span className="font-bold" data-id="element-5082">{selectedOrders.size}</span>{' '}
                orden(es)
              </span>
              <div className="h-5 w-px bg-gray-600 dark:bg-gray-300" data-id="element-5083" />
              <button onClick={() => setShowBulkStatusModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors whitespace-nowrap text-white" data-id="element-5084">
                <Check size={14} data-id="element-5085" />
                Cambiar Estado
              </button>
              <button onClick={() => handleBulkStatusChange('Cancelado')} disabled={bulkProcessing} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50 text-white" data-id="element-5086">
                <XCircle size={14} data-id="element-5087" />
                Anular
              </button>
              <button onClick={() => setSelectedOrders(new Set())} className="text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-gray-900 text-sm transition-colors whitespace-nowrap" data-id="element-5088">
                Cancelar
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Bulk Status Change Modal */}
      {showBulkStatusModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5089">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-5090">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800" data-id="element-5091">
              <div className="flex items-center gap-3" data-id="element-5092">
                <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl" data-id="element-5093">
                  <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-5094" />
                </div>
                <div data-id="element-5095">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5096">
                    Cambio Masivo de Estado
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5097">
                    {selectedOrders.size} orden(es) seleccionada(s)
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-5098">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-5099">
                Selecciona el nuevo estado para todas las órdenes:
              </p>
              <div className="space-y-2" data-id="element-5100">
                {['Pendiente', 'En Preparación', 'Listo', 'Entregado'].map(status => {
              const colors = getStatusColor(status);
              return <button key={status} onClick={() => handleBulkStatusChange(status)} disabled={bulkProcessing} className="w-full p-3 rounded-xl text-left font-medium transition-all bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between group" data-id="element-5101">
                        <span className="flex items-center gap-2" data-id="element-5102">
                          <span className={`w-2 h-2 rounded-full ${colors.dot}`} data-id="element-5103" />
                          {status}
                        </span>
                        {bulkProcessing ? <Loader2 size={16} className="animate-spin text-gray-400" data-id="element-5104" /> : <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 transition-colors" data-id="element-5105" />}
                      </button>;
            })}
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50" data-id="element-5106">
              <Button variant="outline" onClick={() => setShowBulkStatusModal(false)} fullWidth disabled={bulkProcessing} data-id="element-5107">
                Cancelar
              </Button>
            </div>
          </motion.div>
        </div>}

      {/* Create Order Modal */}
      {showCreateOrderModal && <CreateOrderModal companies={companies} employees={employees} menuItems={menuItems} onClose={() => setShowCreateOrderModal(false)} onSave={(orderData: any) => {
      try {
        addOrder(orderData);
        addLog('Orden Creada por Admin', `${orderData.name} - ${orderData.company}`, 'order');
        showToast('success', 'Orden creada', 'La orden ha sido creada exitosamente');
        setShowCreateOrderModal(false);
      } catch (error) {
        showToast('error', 'Error', 'No se pudo crear la orden');
      }
    }} data-id="element-5108" />}

      {/* Status Change Modal */}
      <AnimatePresence data-id="element-5109">
        {showStatusModal && selectedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5110">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-5111">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800" data-id="element-5112">
                <div className="flex justify-between items-start" data-id="element-5113">
                  <div data-id="element-5114">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1" data-id="element-5115">
                      Actualizar Estado
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5116">
                      Orden{' '}
                      <span className="font-mono font-semibold" data-id="element-5117">
                        #{selectedOrder.orderNo}
                      </span>{' '}
                      · {selectedOrder.name}
                    </p>
                  </div>
                  <button onClick={() => setShowStatusModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-5118">
                    <X size={18} data-id="element-5119" />
                  </button>
                </div>
              </div>

              <div className="p-8" data-id="element-5120">
                <div className="relative flex justify-between items-center max-w-sm mx-auto" data-id="element-5121">
                  {/* Connecting line */}
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 dark:bg-gray-700 -z-10 rounded-full" data-id="element-5122" />

                  {['Pendiente', 'En Preparación', 'Listo', 'Entregado'].map((status, index) => {
                const statuses = ['Pendiente', 'En Preparación', 'Listo', 'Entregado'];
                const currentIndex = statuses.indexOf(selectedOrder.status);
                const isPast = index < currentIndex;
                const isCurrent = index === currentIndex;
                let dotClass = '';
                let textClass = '';
                if (isPast) {
                  dotClass = 'bg-green-500 text-white ring-4 ring-white dark:ring-gray-900';
                  textClass = 'text-green-600 dark:text-green-400 font-medium';
                } else if (isCurrent) {
                  dotClass = 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900/30 scale-125';
                  textClass = 'text-blue-700 dark:text-blue-400 font-bold';
                } else {
                  dotClass = 'bg-gray-200 dark:bg-gray-700 text-transparent ring-4 ring-white dark:ring-gray-900 hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer';
                  textClass = 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer';
                }
                return <div key={status} className="flex flex-col items-center relative z-10 group" data-id="element-5123">
                          <button onClick={() => !isCurrent && confirmStatusChange(status)} disabled={isCurrent} className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${dotClass}`} title={isCurrent ? 'Estado actual' : `Cambiar a ${status}`} data-id="element-5124">
                            {isPast ? <Check size={16} data-id="element-5125" /> : <div className="w-2.5 h-2.5 rounded-full bg-current opacity-50" data-id="element-5126" />}
                          </button>
                          <button onClick={() => !isCurrent && confirmStatusChange(status)} disabled={isCurrent} className={`absolute top-12 text-[10px] sm:text-xs whitespace-nowrap transition-colors ${textClass}`} data-id="element-5127">
                            {status}
                          </button>
                        </div>;
              })}

                  {/* Active line progress */}
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-green-500 -z-10 rounded-full transition-all duration-500" style={{
                width: `${Math.max(0, ['Pendiente', 'En Preparación', 'Listo', 'Entregado'].indexOf(selectedOrder.status)) / 3 * 100}%`
              }} data-id="element-5128" />
                </div>

                <p className="mt-16 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-5129">
                  Haz clic en un estado para actualizar la orden
                </p>
              </div>

              <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-end" data-id="element-5130">
                <Button variant="outline" onClick={() => setShowStatusModal(false)} data-id="element-5131">
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Cancel Order Modal */}
      <AnimatePresence data-id="element-5132">
        {showCancelModal && selectedOrder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5133">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden relative" data-id="element-5134">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-red-500 to-rose-600" data-id="element-5135" />

              <div className="p-6 pt-8 text-center" data-id="element-5136">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4 relative" data-id="element-5137">
                  <div className="absolute inset-0 rounded-2xl border-2 border-red-500 opacity-20 animate-ping" data-id="element-5138" />
                  <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-5139" />
                </div>

                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-5140">
                  Anular Pedido
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-5141">
                  ¿Estás seguro que deseas anular esta orden?
                  <br data-id="element-5142" />
                  <span className="font-semibold text-red-600 dark:text-red-400" data-id="element-5143">
                    Esta acción no se puede deshacer.
                  </span>
                </p>

                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-left border border-gray-100 dark:border-gray-700" data-id="element-5144">
                  <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200 dark:border-gray-700" data-id="element-5145">
                    <span className="text-sm font-mono font-bold text-gray-500 dark:text-gray-400" data-id="element-5146">
                      #{selectedOrder.orderNo}
                    </span>
                    <span className="text-sm font-bold font-mono text-gray-900 dark:text-white" data-id="element-5147">
                      RD$ {parseFloat(selectedOrder.total).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2" data-id="element-5148">
                    <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-600 dark:text-gray-300" data-id="element-5149">
                      {selectedOrder.name.charAt(0)}
                    </div>
                    <div data-id="element-5150">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5151">
                        {selectedOrder.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5152">
                        {selectedOrder.company}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 line-clamp-2" data-id="element-5153">
                    <span className="font-medium" data-id="element-5154">
                      {selectedOrder.quantity}×
                    </span>{' '}
                    {displayProductName(selectedOrder.product)}
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex gap-3" data-id="element-5155">
                <Button variant="outline" onClick={() => setShowCancelModal(false)} fullWidth data-id="element-5156">
                  Mantener Orden
                </Button>
                <Button onClick={confirmCancel} fullWidth className="bg-red-600 hover:bg-red-700 text-white border-none shadow-sm" data-id="element-5157">
                  Sí, Anular
                </Button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Order Detail Slide Panel */}
      <AnimatePresence data-id="element-5158">
        {detailOrder && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setDetailOrder(null)} className="fixed inset-0 bg-black/50 z-40" data-id="element-5159" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 h-full w-full sm:w-[450px] bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-gray-800" data-id="element-5160">
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800" data-id="element-5161">
                <div data-id="element-5162">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5163">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5164">
                      Detalle de Orden
                    </h2>
                    <span className="text-xs font-mono font-bold bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-lg text-gray-600 dark:text-gray-300" data-id="element-5165">
                      #{detailOrder.orderNo}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5166">
                    {detailOrder.orderDate}
                  </p>
                </div>
                <button onClick={() => setDetailOrder(null)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-5167">
                  <X size={20} data-id="element-5168" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5" data-id="element-5169">
                {/* Status Section */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800" data-id="element-5170">
                  <div className="flex items-center justify-between mb-4" data-id="element-5171">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-5172">
                      Estado del Pedido
                    </h3>
                    {detailOrder.status !== 'Cancelado' && detailOrder.status !== 'Entregado' && canEdit && <Button size="sm" variant="outline" onClick={() => {
                  setSelectedOrder(detailOrder);
                  setShowStatusModal(true);
                }} data-id="element-5173">
                          Cambiar
                        </Button>}
                  </div>

                  {detailOrder.status === 'Cancelado' ? <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800" data-id="element-5174">
                      <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center" data-id="element-5175">
                        <XCircle size={20} className="text-red-600 dark:text-red-400" data-id="element-5176" />
                      </div>
                      <div data-id="element-5177">
                        <p className="font-bold text-red-700 dark:text-red-400 text-sm" data-id="element-5178">
                          Pedido Cancelado
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-500" data-id="element-5179">
                          Este pedido fue anulado
                        </p>
                      </div>
                    </div> : <div className="flex flex-col gap-0" data-id="element-5180">
                      {['Pendiente', 'En Preparación', 'Listo', 'Entregado'].map((step, idx) => {
                  const steps = ['Pendiente', 'En Preparación', 'Listo', 'Entregado'];
                  const currentIdx = steps.indexOf(detailOrder.status);
                  const isCompleted = idx < currentIdx;
                  const isCurrent = idx === currentIdx;
                  return <div key={step} className="flex items-start gap-3" data-id="element-5181">
                            <div className="flex flex-col items-center" data-id="element-5182">
                              <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center ${isCompleted ? 'bg-green-500 border-green-500' : isCurrent ? 'bg-blue-500 border-blue-500 ring-4 ring-blue-100 dark:ring-blue-900/30' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'}`} data-id="element-5183">
                                {isCompleted && <Check size={10} className="text-white" data-id="element-5184" />}
                              </div>
                              {idx < steps.length - 1 && <div className={`w-0.5 h-6 ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-5185" />}
                            </div>
                            <div className={`pb-4 ${idx === steps.length - 1 ? 'pb-0' : ''}`} data-id="element-5186">
                              <p className={`text-sm font-medium ${isCompleted ? 'text-green-700 dark:text-green-400' : isCurrent ? 'text-blue-700 dark:text-blue-400 font-bold' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-5187">
                                {step}
                                {isCurrent && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded" data-id="element-5188">
                                    Actual
                                  </span>}
                              </p>
                            </div>
                          </div>;
                })}
                    </div>}
                </div>

                {/* Customer Info */}
                <div data-id="element-5189">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-5190">
                    Cliente
                  </h3>
                  <div className="flex items-center gap-3" data-id="element-5191">
                    <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold" data-id="element-5192">
                      {detailOrder.name.charAt(0)}
                    </div>
                    <div data-id="element-5193">
                      <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5194">
                        {detailOrder.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-5195">
                        <Building2 size={14} data-id="element-5196" />
                        {detailOrder.company}
                        {detailOrder.branch ? ` · ${detailOrder.branch}` : ''}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div data-id="element-5197">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-5198">
                    Producto
                  </h3>
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4" data-id="element-5199">
                    <div className="flex items-start gap-3" data-id="element-5200">
                      <div className="p-2.5 bg-gray-100 dark:bg-gray-800 rounded-xl shrink-0" data-id="element-5201">
                        <Package size={20} className="text-gray-600 dark:text-gray-400" data-id="element-5202" />
                      </div>
                      <div data-id="element-5203">
                        <p className="font-medium text-gray-900 dark:text-white leading-snug" data-id="element-5204">
                          {displayProductName(detailOrder.product)}
                        </p>
                        {detailOrder.product.includes('(') && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5205">
                            {detailOrder.product.substring(detailOrder.product.indexOf('('))}
                          </p>}
                        <div className="mt-2 inline-flex items-center px-2.5 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-5206">
                          Cantidad: {detailOrder.quantity}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div data-id="element-5207">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-5208">
                    Resumen Financiero
                  </h3>
                  <div className="bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800 text-sm" data-id="element-5209">
                    <div className="flex justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800" data-id="element-5210">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-5211">
                        Subtotal
                      </span>
                      <span className="font-medium font-mono text-gray-900 dark:text-white" data-id="element-5212">
                        RD${' '}
                        {calculateSubtotal(parseFloat(detailOrder.total), detailOrder.taxExempt).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800" data-id="element-5213">
                      <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1" data-id="element-5214">
                        ITBIS (18%)
                        <Info size={12} className="text-gray-400 cursor-help" title="Impuesto incluido en el precio" data-id="element-5215" />
                      </span>
                      <span className="font-medium font-mono text-gray-900 dark:text-white" data-id="element-5216">
                        {detailOrder.taxExempt ? 'Exento' : `RD$ ${calculateTax(parseFloat(detailOrder.total), detailOrder.taxExempt).toFixed(2)}`}
                      </span>
                    </div>
                    {parseFloat(detailOrder.discount || '0') > 0 && <div className="flex justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800" data-id="element-5217">
                        <span className="text-emerald-600 dark:text-emerald-400" data-id="element-5218">
                          Descuento
                        </span>
                        <span className="font-medium font-mono text-emerald-600 dark:text-emerald-400" data-id="element-5219">
                          - RD$ {parseFloat(detailOrder.discount).toFixed(2)}
                        </span>
                      </div>}
                    {parseFloat(detailOrder.subsidyAmount || '0') > 0 && <div className="flex justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-emerald-50/50 dark:bg-emerald-900/10" data-id="element-5220">
                        <span className="text-emerald-600 dark:text-emerald-400" data-id="element-5221">
                          Subsidio
                        </span>
                        <span className="font-medium font-mono text-emerald-600 dark:text-emerald-400" data-id="element-5222">
                          - RD${' '}
                          {parseFloat(detailOrder.subsidyAmount).toFixed(2)}
                        </span>
                      </div>}
                    <div className="flex justify-between items-center px-4 py-4 bg-gray-900 dark:bg-white" data-id="element-5223">
                      <span className="font-bold text-white dark:text-gray-900" data-id="element-5224">
                        Total
                      </span>
                      <span className="text-2xl font-bold font-mono text-white dark:text-gray-900" data-id="element-5225">
                        RD$ {parseFloat(detailOrder.total).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-5 border-t border-gray-100 dark:border-gray-800 flex gap-3" data-id="element-5226">
                {detailOrder.status !== 'Cancelado' && detailOrder.status !== 'Entregado' && canDelete && <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20" onClick={() => {
              setSelectedOrder(detailOrder);
              setShowCancelModal(true);
              setDetailOrder(null);
            }} data-id="element-5227">
                      Anular
                    </Button>}
                <Button className="flex-1" onClick={() => setDetailOrder(null)} data-id="element-5228">
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}